import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchField } from './SearchField.tsx';

export type PrefixFilterOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type PrefixFilterConfig = {
  options: PrefixFilterOption[];
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  dataTest?: string;
};

export type SearchConfig = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  dataTest?: string;
  debounceMs?: number;
};

export type SearchAndFiltersConfig = {
  search: SearchConfig;
  prefixFilter?: PrefixFilterConfig;
  filters?: ReactNode;
};

export type SearchAndFiltersPanelProps = {
  config: SearchAndFiltersConfig;
};

/**
 * Generic search and filters panel for SelectionDrawer.
 * Config-driven component that renders search with optional prefix filter and filters.
 *
 * @param config - Configuration object containing search, optional prefix filter, and optional filters
 */
export const SearchAndFiltersPanel = ({ config }: SearchAndFiltersPanelProps) => {
  const { t } = useTranslation();
  const { search, prefixFilter, filters } = config;
  const handlePrefixFilterChange = (event: SelectChangeEvent<string>) => {
    prefixFilter?.onChange(event.target.value);
  };

  const prefixElement = prefixFilter ? (
    <Select
      value={prefixFilter.value}
      inputProps={{ 'aria-label': prefixFilter.ariaLabel }}
      onChange={handlePrefixFilterChange}
      data-test={prefixFilter.dataTest ?? 'prefix-filter'}
      sx={{
        textTransform: 'capitalize',
        minWidth: 150,
        maxWidth: 150,
        '&:hover': {
          backgroundColor: 'background.boxLight',
        },
        borderRight: 'none',
        '& .MuiSelect-select': {
          padding: '2px 12px',
        },
      }}
    >
      {prefixFilter.options.map(option => (
        <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  ) : undefined;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          backgroundColor: 'background.boxLight',
          borderBottom: filters ? undefined : '1px solid',
          borderColor: filters ? undefined : 'divider',
          p: 2,
          pb: filters ? 0 : 2,
        }}
      >
        <SearchField
          value={search.value}
          onChange={search.onChange}
          placeholder={search.placeholder ?? t('Search')}
          prefixElement={prefixElement}
          dataTest={search.dataTest ?? 'search-field'}
          debounceMs={search.debounceMs}
        />
      </Box>

      {Boolean(filters) && (
        <Box
          sx={{
            backgroundColor: 'background.boxLight',
            p: 2,
            pl: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {filters}
        </Box>
      )}
    </>
  );
};
